import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "../modeSelect/modeSelect.css";
import "./participants.css";
import { liveId, getParticipantsOnce } from "../../../functions/liveSession";

const Participants = () => {
  const organizationId = useSelector((s) => s.session?.organizationId);
  const sessionId = useSelector((s) => s.session?.sessionId);
  const currentTheme = useSelector(
    (s) => s.theme?.currentTheme || s.theme?.data?.themename || null
  );

  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Polled, not a live onSnapshot listener: this page needs the FULL roster
  // (it's a CSV export of everyone, not a bounded leaderboard), and an
  // unscoped listener on the whole collection re-fires on every single
  // participant's write — fine for occasional admin viewing, but pointless
  // cost at 2000 participants when a periodic refresh looks identical to
  // the admin's eye.
  useEffect(() => {
    if (!organizationId || !sessionId || !currentTheme) {
      setLoading(false);
      return undefined;
    }
    const id = liveId(organizationId, sessionId, currentTheme);
    let cancelled = false;
    const refresh = () => {
      getParticipantsOnce(id)
        .then((list) => {
          if (!cancelled) setParticipants(list);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [organizationId, sessionId, currentTheme]);

  const sorted = [...participants].sort((a, b) => {
    const aJoined = a.joinedAt?.toMillis ? a.joinedAt.toMillis() : 0;
    const bJoined = b.joinedAt?.toMillis ? b.joinedAt.toMillis() : 0;
    return aJoined - bJoined;
  });

  const initials = (name) => {
    const parts = String(name || "").trim().split(/\s+/);
    const first = parts[0]?.[0] || "?";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase();
  };

  // Quote fields that contain commas, quotes, or newlines.
  const escapeCell = (val) => {
    const s = String(val ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  // Formats a Date as DD/MM/YYYY HH:mm:ss in IST (Asia/Kolkata), regardless of local timezone.
  const formatIst = (date) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}:${get("second")}`;
  };

  const handleDownloadCsv = () => {
    if (sorted.length === 0) return;
    const headers = ["name", "identifier", "points", "correct_answers", "answered_count", "joined_at"];
    const rows = sorted.map((p) => {
      const joinedAt = p.joinedAt?.toDate ? formatIst(p.joinedAt.toDate()) : "";
      return [
        p.name || "",
        p.userId || "",
        p.points || 0,
        p.correctCount || 0,
        p.answeredCount || 0,
        joinedAt,
      ];
    });
    const csv = [headers, ...rows].map((row) => row.map(escapeCell).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `participant_${sessionId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="rules-page-new">
      <div className="admin-tabs-holder-v2">
        <div className="tabs-main-title">
          Participants
          <span className="mode-pill">{participants.length} joined</span>
        </div>

        <div className="admin-tab-container">
          <div className="pt-header-row">
            <div className="admin-tab-title">
              <i className="fa-solid fa-users"></i> &nbsp; Who's Joined
            </div>
            {sorted.length > 0 && (
              <button className="pt-download-btn" onClick={handleDownloadCsv}>
                <i className="fa-solid fa-file-csv"></i> Download CSV
              </button>
            )}
          </div>

          {loading ? (
            <div className="code">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="pt-empty">
              <i className="fa-solid fa-user-group"></i>
              <p>No one has joined this session yet.</p>
            </div>
          ) : (
            <div className="pt-list">
              {sorted.map((p) => (
                <div className="pt-row" key={p.userId}>
                  <span className="pt-avatar">{initials(p.name)}</span>
                  <span className="pt-name">{p.name || p.userId}</span>
                  <span className="pt-stats">
                    {p.points || 0} pts · {p.correctCount || 0} correct
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Participants;
